/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { runInAction, transaction } from 'mobx';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { batchPromises } from 'src/core/utils/batchPromises';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import {
    BatchAddExampleSwagger,
    SuggestedUtteranceSwagger,
    suggestedUtteranceSwaggerToSuggestedUtteranceStore
} from 'src/luis/projects/utterances/services/mappers';
import { getSuggestedUtteranceService } from 'src/luis/projects/utterances/services/suggestedUtteranceService';
import { getUtteranceService } from 'src/luis/projects/utterances/services/utteranceService';
import { UtteranceStore } from 'src/luis/projects/utterances/stores/utteranceStore';
import { toLabelsSwagger } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { LuisId } from 'src/luis/types/Resource';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';

export type SuggestedUtteranceHandlerGroup = ReturnType<typeof createSuggestedUtteranceHandlerGroup>;

/**
 * Creates a handler group of functions for the suggested endpoint utterances to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createSuggestedUtteranceHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const logger = getLogger();

    /**
     * Loads suggested endpoint utterances that belong to an app version.
     */
    const loadSuggestedUtterances = async ({
        resource,
        resourceId,
        roleId,
        context,
        localUtterancesMapStore
    }: HandlerParams<{
        resource: 'entities' | 'intents';
        resourceId: LuisId;
        roleId?: LuisId;
        localUtterancesMapStore: Map<LuisId, UtteranceStore>;
    }>) => {
        const { appId, versionId } = context;
        const suggestedUtteranceService = getSuggestedUtteranceService({ context });
        const appVersionStore = rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);
        let suggestedUtteranceSwaggerArr: SuggestedUtteranceSwagger[];

        appVersionStore.suggestedUtteranceStoreHouse.setIsLoading(true);

        const logTimedActivity = logger.createTimedActivityLogger<SuggestedUtteranceSwagger[]>('LoadSuggestedUtterances', () => ({
            resourceId,
            roleId
        }));

        try {
            if (roleId) {
                suggestedUtteranceSwaggerArr = await logTimedActivity(
                    suggestedUtteranceService.getSuggestedUtterancesByRoles(resourceId, roleId)
                );
            } else {
                suggestedUtteranceSwaggerArr = await logTimedActivity(
                    suggestedUtteranceService.getSuggestedUtterances(resource, resourceId)
                );
            }

            const items = suggestedUtteranceSwaggerArr.map(suggestedUtteranceSwaggerToSuggestedUtteranceStore);

            transaction(() => {
                appVersionStore.suggestedUtteranceStoreHouse.setItems(items);

                runInAction(() => {
                    localUtterancesMapStore.clear();

                    items.forEach(utterance => {
                        const utteranceStore = new UtteranceStore(utterance.id, {
                            labeledEntities: [],
                            labeledIntentId: utterance.predictedIntents[0].intentId,
                            createdDate: new Date(),
                            modifiedDate: new Date(),
                            text: utterance.text,
                            tokenizedText: utterance.tokenizedText
                        });

                        localUtterancesMapStore.set(utterance.id, utteranceStore);
                    });
                });
            });
        } catch {
            transaction(() => {
                appVersionStore.suggestedUtteranceStoreHouse.setItems([]);

                runInAction(() => {
                    localUtterancesMapStore.clear();
                });
            });
        }

        appVersionStore.suggestedUtteranceStoreHouse.setIsLoading(false);
        appVersionStore.suggestedUtteranceStoreHouse.setIsLoaded(true);
    };

    /**
     * Deletes the utterance with the given ids from the backend.
     *
     * @param utteranceId The id of the utterance to be deleted.
     */
    const deleteSuggestedUtterances = async ({
        utteranceIds,
        context,
        generateMultiFailureMessage
    }: HandlerParams<{ utteranceIds: LuisId[]; generateMultiFailureMessage: (ids: LuisId[]) => string }>) => {
        const { appId, versionId } = context;
        const suggestedUtteranceService = getSuggestedUtteranceService({ context });
        const appVersionStore = rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);
        const utterances = utteranceIds.map(id => appVersionStore.suggestedUtteranceStoreHouse.findById(id));
        const logTimedActivity = logger.createTimedActivityLogger('DeleteSuggestedUtterance', () => ({ utteranceIds }));

        const { results } = await batchPromises(
            utterances.map(u => logTimedActivity(suggestedUtteranceService.deleteSuggestedUtterance(u.text)))
        );

        const succeededIds = results.filter(res => res.status === 'success').map((_, index) => utteranceIds[index]);
        const failedIds = results.filter(res => res.status === 'error').map((_, index) => utteranceIds[index]);

        appVersionStore.suggestedUtteranceStoreHouse.batchRemoveByIds(succeededIds);

        if (failedIds.length > 0) {
            throw new Error(generateMultiFailureMessage(failedIds));
        }
    };

    /**
     * Add a suggested utterance to the app's utterances.
     */
    const addSuggestedUtterancesToIntent = async ({
        context,
        utterances
    }: HandlerParams<{
        utterances: { id: LuisId; labels: EntityLabelStore[]; predictedIntentId: LuisId }[];
    }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const { entityStoreHouse, intentStoreHouse, suggestedUtteranceStoreHouse } = rootStore.luisAppStoreHouse
                .findById(appId)
                .appVersionStoreHouse.findById(versionId);

            const utteranceIds = utterances.map(u => u.id);
            const utteranceService = getUtteranceService({ context });
            const suggestedUtteranceService = getSuggestedUtteranceService({ context });

            const logAddUtterancesTimedActivity = logger.createTimedActivityLogger<BatchAddExampleSwagger>(
                'AddSuggestedUtteranceToUtterances',
                () => ({ ...context, utteranceIds })
            );

            const payload = utterances.map(({ predictedIntentId, id, labels }) => {
                const suggestedUtterance = suggestedUtteranceStoreHouse.findById(id);
                const intent = intentStoreHouse.findById(predictedIntentId);
                const enableSendingLabelsInNestedFormat = true;

                return {
                    text: suggestedUtterance.text,
                    intentName: intent.name,
                    entityLabels: toLabelsSwagger(
                        {
                            entityStores: entityStoreHouse.items,
                            text: suggestedUtterance.text,
                            tokenizedText: suggestedUtterance.tokenizedText,
                            labels
                        },
                        enableSendingLabelsInNestedFormat
                    )
                };
            });

            const submitResultsArr = (
                await logAddUtterancesTimedActivity(
                    utteranceService.addUtterances({
                        payload,
                        enableSendingLabelsInNestedFormat: true
                    })
                )
            ).map((u, index) => ({ hasError: u.hasError, id: index, errorMessage: u.error?.message }));

            const succeededIds = submitResultsArr.filter(u => !u.hasError).map(u => u.id);
            const failedUtterances = submitResultsArr.filter(u => u.hasError);

            const deleteResultsArr = (
                await batchPromises(
                    succeededIds.map(async id => {
                        const utterance = suggestedUtteranceStoreHouse.findById(id);

                        return suggestedUtteranceService.deleteSuggestedUtterance(utterance.text);
                    })
                )
            ).results.map((res, index) => ({ hasError: res.status === 'error', id: succeededIds[index] }));

            const toBeDeletedUtterances = deleteResultsArr.filter(res => !res.hasError).map(u => u.id);

            suggestedUtteranceStoreHouse.batchRemoveByIds(toBeDeletedUtterances);

            if (failedUtterances.length > 0) {
                const errorMessage = failedUtterances.reduce((acc, u) => (u.hasError ? `${acc} \n - ${u.errorMessage}` : ''), '');
                throw new Error(errorMessage);
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return { loadSuggestedUtterances, deleteSuggestedUtterances, addSuggestedUtterancesToIntent };
};
